import React, { useState } from 'react';
import { Btn, XpBar, STitle, BottomNav } from './ui';
import { C, CIVS, DIFFS } from '../data/constants';
import { getLevel } from '../data/player';
import { isConfigured } from '../lib/supabase';

const DIFF_LABEL = { apprentice: 'Easy', scholar: 'Medium', sage: 'Hard' };
const DIFF_DOT   = { apprentice: '#22C55E', scholar: '#F59E0B', sage: '#EF4444' };

const MODES = [
  { key: 'solo',  icon: '🏺', label: 'Solo'  },
  { key: 'duel',  icon: '⚔️', label: 'Duel'  },
  { key: 'daily', icon: '🌅', label: 'Daily' },
];

export default function Home({
  onPlay, onDuel, onSignIn, onTab, activeTab,
  onJoinOpenDuel,
  selCiv, setSelCiv, selDiff, setSelDiff,
  player, setUsername, user, pendingChallengeCount = 0,
  openDuels = [],
}) {
  const [selMode,   setSelMode]   = useState('solo');
  const [localName, setLocalName] = useState(player?.username || '');

  const lvl = getLevel(player?.totalScore || 0);

  const handlePlay = () => {
    if (localName.trim()) setUsername(localName.trim());
    if (selMode === 'duel') { onDuel(); return; }
    onPlay();
  };

  const displayName = user
    ? (player?.username || user.email?.split('@')[0] || 'Scholar')
    : (player?.username || null);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: '#fff', padding: '12px 16px', borderBottom: `2px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 13, color: C.muted, fontWeight: 500 }}>
            {displayName ? `Welcome back, ${displayName} 👋` : 'Welcome back 👋'}
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: C.amber, letterSpacing: '.05em' }}>KABAAK</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Pill bg="#FFF0F2" border={C.amber} color={C.amber}>🔥 {player?.bestStreak || 0}</Pill>
          <Pill bg={C.goldLight} border="#E8D840" color="#8A7000">⭐ {player?.totalScore || 0}</Pill>
          {isConfigured && (
            <div
              onClick={() => user ? onTab('profile') : onSignIn()}
              style={{ width: 32, height: 32, borderRadius: '50%', background: user ? '#E8F8F0' : '#F5F0EE', border: `2px solid ${user ? C.teal : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, cursor: 'pointer', flexShrink: 0 }}
              title={user ? 'My Profile' : 'Sign In'}>
              {user ? '👤' : '🔑'}
            </div>
          )}
        </div>
      </div>

      <div className="scroll-area" style={{ padding: '14px 16px' }}>
        {/* Daily banner */}
        <div style={{ background: `linear-gradient(135deg,${C.amber},#ba1e4d)`, borderRadius: 16, padding: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10, boxShadow: `0 4px 0 #6a1020` }}>
          <div style={{ fontSize: 28 }}>🌅</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: '#fff' }}>Daily Challenge is Live!</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,.85)' }}>Play today before it resets at midnight</div>
          </div>
          <button onClick={onPlay} style={{ padding: '8px 12px', background: '#fff', color: C.amber, border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, sans-serif' }}>
            Play!
          </button>
        </div>

        <XpBar pct={lvl.pct} label={lvl.title} nextLabel={lvl.next || 'MAX LEVEL'} xpLeft={lvl.xpLeft} />

        {/* Open duels strip — always visible when Supabase is configured */}
        {isConfigured && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <STitle style={{ marginBottom: 0 }}>⚔️ Open Duels</STitle>
              {openDuels.length > 0 && (
                <span style={{ fontSize: 11, color: C.muted }}>{openDuels.length} waiting</span>
              )}
            </div>

            {openDuels.length === 0 ? (
              <div style={{ background: '#fff', border: `2px dashed ${C.border}`, borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: C.faint }}>No open duels right now — create one!</div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4 }}
                   className="scroll-area" >
                {openDuels.map(d => {
                  const civ = CIVS.find(c => c.key === d.civ) || CIVS[0];
                  const dotColor = DIFF_DOT[d.diff] || C.muted;
                  return (
                    <div key={d.code} style={{ flexShrink: 0, width: 120, background: '#fff', border: `2px solid ${C.border}`, borderRadius: 14, padding: '10px 8px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, boxShadow: `0 3px 0 ${C.border}` }}>
                      <div style={{ fontSize: 26 }}>{civ.icon}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: C.text, textAlign: 'center', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.host_username || 'Scholar'}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />
                        <span style={{ fontSize: 10, color: C.muted }}>{DIFF_LABEL[d.diff] || d.diff}</span>
                      </div>
                      <button
                        onClick={() => user ? onJoinOpenDuel?.(d.code) : onSignIn?.()}
                        style={{ marginTop: 4, width: '100%', padding: '6px 0', background: user ? C.amber : '#F5F0EE', color: user ? '#fff' : C.muted, border: 'none', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, sans-serif' }}>
                        {user ? 'Join →' : 'Sign in'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Mode */}
        <STitle>Mode</STitle>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {MODES.map(m => {
            const active = selMode === m.key;
            return (
              <div key={m.key} onClick={() => setSelMode(m.key)}
                style={{ flex: 1, background: active ? C.goldLight : '#fff', border: `2px solid ${active ? C.amber : C.border}`, borderRadius: 12, padding: '10px 6px', textAlign: 'center', cursor: 'pointer', boxShadow: `0 3px 0 ${active ? '#6a1020' : C.border}`, transition: 'all .15s' }}>
                <div style={{ position: 'relative', display: 'inline-block', marginBottom: 2 }}>
                  <div style={{ fontSize: 20 }}>{m.icon}</div>
                  {m.key === 'duel' && pendingChallengeCount > 0 && (
                    <div style={{ position: 'absolute', top: -4, right: -6, width: 16, height: 16, borderRadius: '50%', background: '#FF3B30', border: '2px solid #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, color: '#fff' }}>
                      {pendingChallengeCount}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: active ? C.amber : C.muted }}>{m.label}</div>
              </div>
            );
          })}
        </div>

        {selMode === 'duel' && (
          <div style={{ background: '#FFF9E8', border: `2px solid ${C.goldLight}`, borderRadius: 12, padding: '10px 12px', marginBottom: 12, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
            ⚔️ <strong>Live Duel</strong> — Challenge a friend with the same questions in real time.
            {!user && isConfigured && (
              <div style={{ marginTop: 6, fontSize: 12, color: C.muted }}>
                🔑 <span onClick={onSignIn} style={{ color: C.amber, fontWeight: 700, cursor: 'pointer' }}>Sign in</span> to access duels.
              </div>
            )}
          </div>
        )}

        {/* Civilization */}
        <STitle>Civilization</STitle>
        <div style={{ display: 'flex', gap: 7, overflowX: 'auto', paddingBottom: 8, marginBottom: 14 }}>
          {CIVS.map(c => {
            const active = selCiv === c.key;
            return (
              <div key={c.key} onClick={() => setSelCiv(c.key)}
                style={{ flexShrink: 0, minWidth: 68, background: active ? c.bg : '#fff', border: `2px solid ${active ? c.border : C.border}`, borderRadius: 12, padding: '9px 6px', textAlign: 'center', cursor: 'pointer', boxShadow: `0 3px 0 ${active ? c.shadow : C.border}`, transition: 'all .15s' }}>
                <div style={{ fontSize: 22, marginBottom: 2 }}>{c.icon}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: active ? c.text : C.muted }}>{c.label}</div>
              </div>
            );
          })}
        </div>

        {/* Difficulty */}
        <STitle>Difficulty</STitle>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {DIFFS.map(d => {
            const active = selDiff === d.key;
            return (
              <div key={d.key} onClick={() => setSelDiff(d.key)}
                style={{ flex: 1, background: active ? d.activeBg : '#fff', border: `2px solid ${active ? d.activeBorder : C.border}`, borderRadius: 12, padding: '10px 6px', textAlign: 'center', cursor: 'pointer', boxShadow: `0 3px 0 ${active ? d.activeShadow : C.border}`, transition: 'all .15s' }}>
                <div style={{ fontSize: 16, marginBottom: 2 }}>{d.icon}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: active ? d.activeText : C.muted }}>{d.label}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{d.sub}</div>
              </div>
            );
          })}
        </div>

        {/* Name input (only show when not logged in with a profile name) */}
        {!user && (
          <input
            value={localName}
            onChange={e => setLocalName(e.target.value)}
            onBlur={() => localName.trim() && setUsername(localName.trim())}
            placeholder="Your name (for leaderboard & sharing)"
            style={{ width: '100%', padding: 13, background: '#fff', border: `2px solid ${C.border}`, borderRadius: 13, color: C.text, fontSize: 13, fontFamily: 'Roboto, sans-serif', outline: 'none', marginBottom: 12, boxSizing: 'border-box' }}
          />
        )}

        <Btn variant="green" onClick={handlePlay}>🎮 Start Playing!</Btn>
        <div style={{ height: 16 }} />
      </div>

      <BottomNav active={activeTab} onTab={onTab} />
    </div>
  );
}

function Pill({ bg, border, color, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 3, background: bg, border: `2px solid ${border}`, borderRadius: 20, padding: '4px 9px', fontSize: 12, fontWeight: 700, color }}>
      {children}
    </div>
  );
}
